/**
 * ApiError — Central error class for API errors (F012)
 *
 * Thrown by apiClient when HTTP status >= 400.
 * Carries status code and response body for status-specific UI messaging.
 *
 * N1(a): 429 (Rate-Limit) wird explizit modelliert:
 * - `isRateLimit` erkennt den Fall an einer Stelle
 * - `retryAfterSeconds` normalisiert `Retry-After` (Sekunden ODER HTTP-Datum)
 *   bzw. den Fallback aus `X-RateLimit-Reset` / Body-Feld `retry_after`
 *   (Backend liefert dort teils einen absoluten Unix-Timestamp).
 */
export class ApiError extends Error {
  public readonly retryAfterSeconds: number | null

  constructor(
    public readonly status: number,
    public readonly body: unknown,
    public readonly headers?: Headers,
  ) {
    super(`API Error: ${status}`)
    this.name = 'ApiError'
    this.retryAfterSeconds = parseRetryAfter(headers, body)
  }

  get isRateLimit(): boolean {
    return this.status === 429
  }
}

/** Wandelt einen Sekunden- oder Unix-Timestamp-Wert in "Sekunden ab jetzt" um. */
function normalizeSeconds(value: number): number | null {
  if (!Number.isFinite(value) || value < 0) return null
  // Werte > 1e9 sind faktisch absolute Unix-Timestamps (Backend-Fallback
  // X-RateLimit-Reset), keine Wartedauer.
  if (value > 1_000_000_000) {
    const delta = Math.round(value - Date.now() / 1000)
    return delta > 0 ? delta : null
  }
  return Math.round(value)
}

function parseHeaderValue(raw: string | null | undefined): number | null {
  if (!raw) return null
  const trimmed = raw.trim()
  if (trimmed.length === 0) return null

  const numeric = Number(trimmed)
  if (!Number.isNaN(numeric)) return normalizeSeconds(numeric)

  // HTTP-Datum (RFC 7231)
  const asDate = Date.parse(trimmed)
  if (!Number.isNaN(asDate)) {
    const delta = Math.round((asDate - Date.now()) / 1000)
    return delta > 0 ? delta : null
  }
  return null
}

export function parseRetryAfter(headers: Headers | undefined, body?: unknown): number | null {
  const fromHeader =
    parseHeaderValue(headers?.get('Retry-After')) ??
    parseHeaderValue(headers?.get('X-RateLimit-Reset'))
  if (fromHeader !== null) return fromHeader

  // Fallback: Body-Feld { error: { retry_after } }
  if (body && typeof body === 'object') {
    const err = (body as { error?: unknown }).error
    if (err && typeof err === 'object') {
      const raw = (err as { retry_after?: unknown }).retry_after
      if (typeof raw === 'number') return normalizeSeconds(raw)
      if (typeof raw === 'string') return parseHeaderValue(raw)
    }
  }
  return null
}
